import { useMemo } from "react";
import { useWorkspaceDetails } from "@project-mgmt/feature-workspaces";
import { useProjects } from "@project-mgmt/feature-projects";
import { useTasks } from "@project-mgmt/feature-tasks";
import { useNotifications } from "@project-mgmt/feature-notifications";
import { aggregateFlowData } from "../services/dataAdapter";
/**
 * Hook to aggregate data from multiple feature hooks and transform it into flow visualization data
 *
 * This hook:
 * - Fetches the specific workspace details (not all workspaces for security)
 * - Fetches projects, tasks, and notifications for the given workspace
 * - Aggregates loading states from all hooks
 * - Aggregates error states from all hooks
 * - Transforms the data into FlowNode and FlowEdge structures using the dataAdapter service
 * - Returns a FlowData object with nodes, edges, loading state, and errors
 *
 * @param workspaceId - The workspace ID to fetch data for
 * @returns FlowData object containing nodes, edges, loading state, and errors
 */
export function useFlowData(workspaceId) {
    // Fetch data from all feature hooks - only fetch the specific workspace
    const { data: workspaceData, isLoading: workspaceLoading, error: workspaceError } = useWorkspaceDetails(workspaceId);
    const { data: projectsData, isLoading: projectsLoading, error: projectsError } = useProjects(workspaceId);
    const { tasks: tasksData, isLoading: tasksLoading, error: tasksError } = useTasks(workspaceId);
    const { data: notificationsData, isLoading: notificationsLoading, error: notificationsError, } = useNotifications(workspaceId);
    // Aggregate loading states - show loading if any hook is loading
    const isLoading = workspaceLoading || projectsLoading || tasksLoading || notificationsLoading;
    // Aggregate error states
    const errors = {
        workspace: workspaceError || undefined,
        projects: projectsError || undefined,
        tasks: tasksError || undefined,
        notifications: notificationsError || undefined,
    };
    // Transform data using aggregateFlowData service
    // Use useMemo to avoid recalculating on every render
    const { nodes, edges } = useMemo(() => {
        // Return empty data if any required data is missing
        if (!workspaceData?.workspace || !projectsData?.projects) {
            return { nodes: [], edges: [] };
        }
        // Use the single workspace and its related data
        const workspace = workspaceData.workspace;
        const projects = projectsData.projects;
        const tasks = tasksData || [];
        const notifications = notificationsData || [];
        return aggregateFlowData(workspace, projects, tasks, notifications);
    }, [workspaceData, projectsData, tasksData, notificationsData]);
    return {
        nodes,
        edges,
        isLoading,
        errors,
    };
}
